import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Sparkles, Zap, Crown, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useCurrency";
import { CurrencySelector } from "@/components/CurrencySelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Plans = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [user, setUser] = useState(null);

  const {
    currency,
    supported,
    detectedCountry,
    setCurrency,
    formatFromInr,
    loading: currencyLoading,
  } = useCurrency();

  useEffect(() => {
    loadPlans();
    loadCurrentSubscription();
    loadUser();
    loadRazorpayScript();
  }, []);

  const loadUser = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (_) {}
  };

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error("Error loading plans:", error);
      toast.error("Failed to load plans");
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription`, {
        withCredentials: true
      });
      setCurrentPlan(response.data);
    } catch (error) {
      console.error("Error loading subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchasePlan = async (planType) => {
    const plan = plans?.[planType];
    if (!plan) return;

    // Free plan: go through the existing (non-payment) mock endpoint
    if (plan.price === 0) {
      try {
        setPurchasing(planType);
        const response = await axios.post(
          `${BACKEND_URL}/api/subscription/purchase`,
          { plan_type: planType },
          { withCredentials: true }
        );
        toast.success(response.data.message);
        await loadCurrentSubscription();
        setTimeout(() => navigate("/dashboard"), 2000);
      } catch (error) {
        console.error("Error selecting free plan:", error);
        toast.error(error.response?.data?.detail || "Failed to select plan");
      } finally {
        setPurchasing(null);
      }
      return;
    }

    try {
      setPurchasing(planType);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway. Please refresh the page.");
        setPurchasing(null);
        return;
      }

      const orderResponse = await axios.post(
        `${BACKEND_URL}/api/razorpay/create-order`,
        {
          amount: plan.price,
          order_type: "plan",
          plan_type: planType,
          currency,
        },
        { withCredentials: true }
      );
      const orderData = orderResponse.data;

      const options = {
        key: orderData.key_id,
        amount: orderData.amount_minor,
        currency: orderData.currency,
        name: "LoopAI Creator",
        description: orderData.description,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${BACKEND_URL}/api/razorpay/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { withCredentials: true }
            );
            if (verifyRes.data.success) {
              toast.success(verifyRes.data.message);
              await loadCurrentSubscription();
              setTimeout(() => navigate("/dashboard"), 2000);
            }
          } catch (err) {
            console.error("Payment verification error:", err);
            toast.error("Payment verification failed. Please contact support.");
          } finally {
            setPurchasing(null);
          }
        },
        prefill: {
          name: orderData.prefill?.name || user?.name || "",
          email: orderData.prefill?.email || user?.email || "",
        },
        theme: { color: "#7c3aed" },
        modal: {
          ondismiss: () => {
            setPurchasing(null);
            toast.info("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Unknown error"}`);
        setPurchasing(null);
      });
      rzp.open();
    } catch (error) {
      console.error("Error purchasing plan:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setPurchasing(null);
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case "pay_as_you_go":
        return <Zap className="w-8 h-8 text-blue-500" />;
      case "starter_pack":
        return <Sparkles className="w-8 h-8 text-purple-500" />;
      case "creator_pack":
        return <Crown className="w-8 h-8 text-yellow-500" />;
      case "studio_pack":
        return <Crown className="w-8 h-8 text-pink-500" />;
      default:
        return <Zap className="w-8 h-8 text-gray-500" />;
    }
  };

  const isCurrentPlan = (planType) => {
    return currentPlan?.plan_type === planType;
  };

  if (loading || !plans) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-2"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Choose Your Plan</h1>
              <p className="text-gray-600 mt-2">
                Select the plan that best fits your needs. Prices shown in <span className="font-semibold">{currency}</span>.
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-1">
              <CurrencySelector
                value={currency}
                supported={supported}
                onChange={setCurrency}
                detectedCountry={detectedCountry}
              />
              {currency !== "INR" && !currencyLoading && (
                <p className="text-xs text-gray-500" data-testid="fx-note">
                  Charged in {currency} via Razorpay international checkout.
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Current Plan Badge */}
      {currentPlan && currentPlan.plan_type !== "pay_as_you_go" && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-semibold">
              Current Plan: {currentPlan.plan_name}
              {currentPlan.expires_at && (
                <span className="ml-2 text-sm font-normal">
                  (Expires: {new Date(currentPlan.expires_at).toLocaleDateString()})
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(plans).map(([planType, plan]) => (
            <Card
              key={planType}
              className={`relative border-2 transition-all ${
                isCurrentPlan(planType)
                  ? "border-blue-500 shadow-xl"
                  : "border-gray-200 hover:border-blue-300 hover:shadow-lg"
              }`}
            >
              {isCurrentPlan(planType) && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl-lg rounded-tr-lg font-semibold">
                  ACTIVE
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4">
                  {getPlanIcon(planType)}
                </div>
                <CardTitle className="text-xl mb-2">{plan.name}</CardTitle>
                <div className="text-3xl font-bold text-gray-900" data-testid={`plan-price-${planType}`}>
                  {plan.price === 0 ? (
                    <span>Free</span>
                  ) : (
                    <>
                      <span>{formatFromInr(plan.price)}</span>
                      {plan.billing_cycle && (
                        <span className="text-sm font-normal text-gray-500">/month</span>
                      )}
                    </>
                  )}
                </div>
                {plan.price > 0 && currency !== "INR" && (
                  <p className="text-xs text-gray-400 mt-1" data-testid={`plan-price-inr-${planType}`}>
                    ≈ ₹{plan.price.toLocaleString()} INR
                  </p>
                )}
                {plan.credits > 0 && (
                  <CardDescription className="text-sm mt-2">
                    {plan.credits.toLocaleString()} credits
                  </CardDescription>
                )}
              </CardHeader>
              
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => handlePurchasePlan(planType)}
                  disabled={isCurrentPlan(planType) || purchasing === planType}
                  className={`w-full ${
                    planType === "creator_pack"
                      ? "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                      : planType === "studio_pack"
                      ? "bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                      : "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                  }`}
                  data-testid={`purchase-${planType}-btn`}
                >
                  {purchasing === planType
                    ? "Processing..."
                    : isCurrentPlan(planType)
                    ? "Current Plan"
                    : planType === "pay_as_you_go"
                    ? "Free Plan"
                    : `Purchase — ${formatFromInr(plan.price)}`}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Feature Comparison */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">Feature Comparison</h2>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Pay as you go</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Starter Pack</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Creator Pack</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Studio Pack</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Image Generation</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">Video Generation</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Avatar Tools</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">Content Planner</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Advanced Editor</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-700">Priority Generation</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-700">Dedicated Manager</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center text-gray-400">-</td>
                    <td className="px-6 py-4 text-center"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Plans;
